import { EditOutlined } from "@mui/icons-material";
import { Box, IconButton, type SxProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { DateTime as LuxonDateTime } from "luxon";
import type { FC, MouseEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { INK_BLACK } from "../../theme/tokens";
import { CENSIUS_EDIT_EXTRA_DAYS } from "../../utils/consts";
import type { ChangesSource } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { ThemedSvg } from "../UIToolkit/ThemedSvg";
import { EditCensusMenu } from "./EditCensusMenu";

type CensusDisplayProps = {
	source: ChangesSource;
	dayDate: DateTime;
	/** Main census text content (e.g. number, draft diff block, etc.) */
	censusContent?: ReactNode;
	/** Optional tooltip content to show on hover. */
	tooltipContent?: ReactNode;
	/** Optional click handler for the main area (excluding the edit icon). */
	onClickMain?: () => void;
	/** Whether to show the bed icon on the left. Defaults to true. */
	showBedIcon?: boolean;
	/** Optional styles applied to the root Box. */
	containerSx?: SxProps;
	/** Optional styles applied to the IconButton. */
	iconButtonSx?: SxProps;
	/** Optional overriding census value to display. If not provided, census is calculated from hospitalData. */
	censusValue: number;
	displayTitle?: boolean;
	isBlack?: boolean;
	censusSx?: SxProps;
};

export const CensusDisplay: FC<CensusDisplayProps> = ({
	source,
	dayDate,
	censusContent,
	tooltipContent,
	onClickMain,
	showBedIcon = true,
	containerSx,
	iconButtonSx,
	censusValue,
	displayTitle,
	isBlack = false,
	censusSx,
}) => {
	const theme = useTheme();
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

	const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
	};

	const handleMenuClose = (): void => {
		setMenuAnchorEl(null);
	};

	const isEditable = useMemo(() => {
		return dayDate <= LuxonDateTime.now().plus({ days: CENSIUS_EDIT_EXTRA_DAYS }).endOf("day");
	}, [dayDate]);

	const content = (
		<Box
			data-testid={`census-display-${dayDate.toISODate()}`}
			sx={{
				display: "flex",
				alignItems: "center",
				...(containerSx || {}),
			}}
			onClick={(e) => {
				e.stopPropagation();
				onClickMain?.();
			}}
		>
			{showBedIcon && <ThemedSvg src={isBlack ? "/bed-dark.svg" : "/bed.svg"} alt="Bed" size={18} color={isBlack ? INK_BLACK : "var(--inhouse-primary)"} />}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					marginLeft: showBedIcon ? "5px" : 0,
				}}
			>
				{censusContent ? (
					censusContent
				) : displayTitle ? (
					<Box sx={{ gap: 1.2, display: "flex" }}>
						<Typography sx={{ color: isBlack ? theme.palette.primary.light : theme.palette.primary.dark }}>Census</Typography>
						<Typography
							sx={{
								fontWeight: "regular",
								color: theme.palette.primary.dark,
							}}
						>
							{censusValue.toFixed(0)}
						</Typography>
					</Box>
				) : (
					<Typography sx={{ color: isBlack ? INK_BLACK : theme.palette.primary.dark, fontSize: "14px", marginLeft: "5px", ...(censusSx || {}) }}>
						{censusValue.toFixed(0)}
					</Typography>
				)}
				{isEditable && (
					<IconButton
						data-testid="census-edit-button"
						onClick={handleMenuOpen}
						sx={{
							p: 0,
							marginLeft: "2px",
							...(iconButtonSx || {}),
						}}
					>
						<EditOutlined
							sx={{
								fontSize: "16px",
								cursor: "pointer",
								color: "primary.main",
							}}
						/>
					</IconButton>
				)}
			</Box>
		</Box>
	);

	return (
		<>
			<EditCensusMenu dayDate={dayDate} handleMenuClose={handleMenuClose} menuAnchorEl={menuAnchorEl} source={source} />

			{tooltipContent ? (
				/*
				 * `disableInteractive` because the breakdown is read, never clicked. As an interactive popper it
				 * kept pointer capture after the pointer left the number, and it sits directly over the day
				 * card's action icons -- so a click aimed at those landed on the tooltip instead and read to the
				 * user as a click that simply didn't work.
				 */
				<InHouseTooltip title={tooltipContent} disableInteractive>
					<span>{content}</span>
				</InHouseTooltip>
			) : (
				content
			)}
		</>
	);
};
